import {ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot} from '@angular/router';
import {inject} from '@angular/core';
import {Observable, catchError, of} from 'rxjs';
import {UserService} from '../services/user/user.service';
import {UrlManager} from '../lib/url-manager';
import {UrlParamLiterals} from '../enums/literals/urlparams.enum';
import {Views} from '@ccp/config/views';
import {CustomerErrorCode} from '../enums/customer-error-code.enum';
import {take, tap} from 'rxjs/operators';
import {TokenValidity} from '@ccp/struct/tokenValidity';
import {WebStorage} from '@ccp/lib/web-storage';
import {WSLiterals} from '@ccp/enums/literals/webstorage.enum';
import {WebStorageType} from '@ccp/enums/web-storage-type.enum';
import {Logger} from '@ccp/lib/logger';
import {LocaleContextService} from '@ccp/services/app-context/locale-context.service';

export const TokenValidityResolver: ResolveFn<Observable<boolean | TokenValidity>> = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
  userService: UserService = inject(UserService),
  localeContextService: LocaleContextService = inject(LocaleContextService),
  router: Router = inject(Router),
): Observable<boolean | TokenValidity> => {
  const params = UrlManager.getParams();
  const token = params[UrlParamLiterals.Token] ?? '';
  const tokenValidityType = route.data['tokenValidityType'];

  if (localeContextService.currentCountry$().isChina) {
    return of(true);
  }

  if (WebStorage.isExist(WSLiterals.tokenValidity, WebStorageType.sessionStorage)) {
    try {
      const storeToken = JSON.parse(WebStorage.get(WSLiterals.tokenValidity, WebStorageType.sessionStorage));
      WebStorage.remove(WSLiterals.tokenValidity, WebStorageType.sessionStorage);
      return of(storeToken);
    } catch {
      Logger.debug('invalid validity token');
    }
  }

  return userService.tokenValidity(token, tokenValidityType).pipe(
    take(1),
    tap((token: TokenValidity) => WebStorage.set(WSLiterals.tokenValidity, JSON.stringify(token), WebStorageType.sessionStorage)),
    catchError((e) => {
      const callbackUrl = UrlManager.setParams(UrlManager.getParams('', [UrlParamLiterals.Token]), UrlManager.getCurrentPath(true, false));
      const country = route.queryParamMap.get(UrlParamLiterals.Country) || route.queryParamMap.get(UrlParamLiterals.Location);
      Logger.error(`Token expired - country: ${country}, validity type: ${tokenValidityType}`, {error: e});
      void router.navigate([Views.profileVerifyError.path], {
        queryParams: {
          code: CustomerErrorCode.TokenExpired,
          country: country,
          callbackUrl: callbackUrl,
          tokenValidityType: tokenValidityType,
        },
      });
      return of(false);
    }),
  );
};
